import { useState } from 'react';
// F-3 · ComplianceSteps.tsx — renders buildComplianceSteps output (§6), plus
// the §8.1 personnel-lift advisory banner (Z19: advisory-only, never a
// first-class scenario) and the §10.2 50%-WLL note copy.
import type { ComplianceStep, ClassificationResult, LiftDetail } from '@/types/planData';
import {
  buildComplianceSteps,
  PERSONNEL_LIFT_BANNER,
  WLL_50_PERCENT_NOTE,
} from '@/utils/complianceSteps';

export interface ComplianceStepsProps {
  classification: ClassificationResult | null;
  liftDetails: LiftDetail[];
  jhaReference?: string | null;
  anyPersonnelLift: boolean;
  steps?: ComplianceStep[]; // optional override (e.g. stored steps for review)
}

function LongRequirement({ requirement, footnote }: { requirement: string; footnote: string | null }) {
  const [expanded, setExpanded] = useState(false);
  const summary = requirement.length > 110 ? requirement.slice(0, 110).replace(/[,;:]\s*$/, '') + '…' : requirement;
  return (
    <div className="mt-1" data-testid="long-requirement">
      {expanded ? (
        <>
          <p className="text-sm text-gray-800">{requirement}</p>
          {footnote && <p className="text-xs text-gray-500 italic mt-1">{footnote}</p>}
          <button type="button" onClick={() => setExpanded(false)} className="text-xs text-blue-700 underline mt-1">Collapse</button>
        </>
      ) : (
        <div className="flex items-baseline gap-2">
          <p className="text-sm text-gray-800 flex-1">{summary}</p>
          <button type="button" onClick={() => setExpanded(true)} className="text-xs text-blue-700 underline whitespace-nowrap">Show full text</button>
        </div>
      )}
    </div>
  );
}

const STATUS_STYLES: Record<ComplianceStep['status'], string> = {
  pending: 'bg-amber-100 text-amber-800',
  documented: 'bg-green-100 text-green-800',
  na: 'bg-gray-100 text-gray-600',
};

export function ComplianceSteps({
  classification,
  liftDetails,
  jhaReference = null,
  anyPersonnelLift,
  steps,
}: ComplianceStepsProps) {
  const resolved =
    steps && steps.length > 0
      ? steps
      : buildComplianceSteps(classification, liftDetails, { jhaReference });

  return (
    <div className="space-y-3" data-testid="compliance-steps">
      {/* §8.1 personnel-lift advisory banner (Z19) */}
      {anyPersonnelLift && (
        <div
          className="bg-blue-50 border border-blue-300 rounded-lg p-3 text-sm text-blue-900"
          data-testid="personnel-lift-banner"
        >
          {PERSONNEL_LIFT_BANNER}
        </div>
      )}
      {/* §10.2 50%-WLL note (personnel lifts only — VERIFIED wording) */}
      {anyPersonnelLift && (
        <div
          className="bg-amber-50 border border-amber-300 rounded-lg p-3 text-sm text-amber-900"
          data-testid="wll-50-percent-note"
        >
          {WLL_50_PERCENT_NOTE}
        </div>
      )}

      {resolved.map((step, i) => (
        <div key={`${step.ref}-${i}`} className="border border-gray-200 rounded-lg p-3" data-testid="compliance-step">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-xs text-gray-500">{step.ref}</span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${STATUS_STYLES[step.status]}`}>
              {step.status}
            </span>
          </div>
          {/* Long requirement text (the §4.4.5.1 a–p wall) collapses to a
              one-line summary + 'Show full text' — Brian 2026-09-09: it took
              a whole phone screen while scrolling. */}
          {step.requirement.length > 220 ? (
            <LongRequirement requirement={step.requirement} footnote={step.fields?.['footnote'] != null ? String(step.fields['footnote']) : null} />
          ) : (
            <>
              <p className="text-sm text-gray-800 mt-1">{step.requirement}</p>
              {step.fields?.['footnote'] != null && (
                <p className="text-xs text-gray-500 italic mt-1">{String(step.fields['footnote'])}</p>
              )}
            </>
          )}
          {step.fields && step.ref.includes('TIA-322-A') && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-2" data-testid="qe-fields">
              {['qeName', 'qeFirm', 'evaluationReport'].map((key) => (
                <input
                  key={key}
                  type="text"
                  placeholder={key === 'qeName' ? 'QE Name' : key === 'qeFirm' ? 'QE Firm' : 'Evaluation report ref'}
                  defaultValue={String(step.fields?.[key] ?? '')}
                  className="px-2 py-1 border border-gray-300 rounded text-sm"
                />
              ))}
            </div>
          )}
          {step.evidence && (
            <p className="text-xs text-gray-600 mt-1">
              Evidence: {step.evidence}
              {step.by ? ` — by ${step.by}` : ''}
              {step.at ? ` at ${step.at}` : ''}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
